import { isHeicAsset } from './heic';
import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import type { CreativeTab } from './model';
import {
  type Creative,
  creativeBannerSizeKey,
  findCreativeBannerSize,
  CreativeHostedMode,
  type CreativeMutationPayload,
  CreativeStatus,
  CreativeSubType,
  CreativeType,
  creativeTypeForTab,
  normalizeCreativeHostedMode,
  normalizeCreativeSubType,
  type CreativeVariant,
} from './model';

const optionalUrlSchema = z
  .string()
  .max(255)
  .refine(isValidOptionalUrl, {
    message: dashboardT('creatives.validation.url'),
  });

const creativeFormBaseSchema = z.object({
  name: z.string().trim().min(1, dashboardT('creatives.validation.name')).max(255),
  type: z.enum(CreativeType),
  activeStatus: z.enum(CreativeStatus),
  subType: z.enum(CreativeSubType),
  bannerSize: z.string(),
  content: z.string(),
  clickThroughUrl: optionalUrlSchema,
  width: z.string(),
  height: z.string(),
  hosted: z.enum(CreativeHostedMode),
  vastTag: z.string(),
  vastUrl: z.string(),
  impressionTrackers: z.array(optionalUrlSchema),
  trackerStart: optionalUrlSchema,
  trackerFirstQuartile: optionalUrlSchema,
  trackerMidpoint: optionalUrlSchema,
  trackerThirdQuartile: optionalUrlSchema,
  trackerComplete: optionalUrlSchema,
  variants: z.array(z.custom<CreativeVariant>()),
  file: z.custom<File | null>((value) => value === null || value instanceof File),
});

export type CreativeFormValue = z.infer<typeof creativeFormBaseSchema>;

export function createCreativeFormSchema({ editMode }: { editMode: boolean }) {
  return creativeFormBaseSchema.superRefine((value, context) => {
    if (value.file && isHeicAsset(value.file.name, value.file.type)) {
      addIssue(context, 'file', dashboardT('creatives.heicUnsupported'));
    }
    const requiresClickThroughUrl =
      value.type === CreativeType.Display
        ? value.subType === CreativeSubType.Image
        : value.hosted === CreativeHostedMode.Shigo;
    if (requiresClickThroughUrl && !value.clickThroughUrl.trim()) {
      addIssue(
        context,
        'clickThroughUrl',
        dashboardT('creatives.validation.clickThroughUrlRequired'),
      );
    }
    if (value.type === CreativeType.Display) {
      if (!isValidOptionalPositiveNumber(value.width)) {
        addIssue(context, 'width', dashboardT('creatives.validation.dimension'));
      }
      if (!isValidOptionalPositiveNumber(value.height)) {
        addIssue(context, 'height', dashboardT('creatives.validation.dimension'));
      }
      if (!editMode && !value.bannerSize) {
        addIssue(context, 'bannerSize', dashboardT('creatives.validation.bannerSize'));
      }
      if (value.subType === CreativeSubType.Html && !value.content.trim()) {
        addIssue(context, 'content', dashboardT('creatives.validation.content'));
      }
      if (!editMode && value.subType === CreativeSubType.Image && !value.file) {
        addIssue(context, 'file', dashboardT('creatives.validation.imageFile'));
      }
      return;
    }
    if (value.hosted === CreativeHostedMode.Shigo && !editMode && !value.file) {
      addIssue(context, 'file', dashboardT('creatives.validation.videoFile'));
    }
    if (value.hosted === CreativeHostedMode.VastTag && !value.vastTag.trim()) {
      addIssue(context, 'vastTag', dashboardT('creatives.validation.vastTag'));
    }
    if (value.hosted === CreativeHostedMode.VastUrl && !isValidOptionalUrl(value.vastUrl)) {
      addIssue(context, 'vastUrl', dashboardT('creatives.validation.url'));
    }
    if (value.hosted === CreativeHostedMode.VastUrl && !value.vastUrl.trim()) {
      addIssue(context, 'vastUrl', dashboardT('creatives.validation.vastUrl'));
    }
  });
}

export function createCreativeFormValue(tab: CreativeTab, creative?: Creative): CreativeFormValue {
  return {
    name: creative?.name ?? '',
    type:
      creative?.type === CreativeType.Display || creative?.type === CreativeType.Video
        ? creative.type
        : creativeTypeForTab(tab),
    activeStatus: creative?.activeStatus ?? CreativeStatus.Active,
    subType: normalizeCreativeSubType(creative?.subType),
    // The API keeps only the dimensions, so an edited creative shows the size they describe.
    bannerSize: creative?.bannerSize
      ? creative.bannerSize
      : creativeBannerSizeKey(creative?.width, creative?.height),
    content: creative?.content ?? '',
    clickThroughUrl: creative?.clickThroughUrl ?? '',
    width: numberString(creative?.width),
    height: numberString(creative?.height),
    hosted: normalizeCreativeHostedMode(creative?.hosted),
    vastTag: creative?.vastTag ?? '',
    vastUrl: creative?.vastUrl ?? '',
    impressionTrackers: [...(creative?.impressionTrackers ?? [])],
    trackerStart: creative?.trackerStart ?? '',
    trackerFirstQuartile: creative?.trackerFirstQuartile ?? '',
    trackerMidpoint: creative?.trackerMidpoint ?? '',
    trackerThirdQuartile: creative?.trackerThirdQuartile ?? '',
    trackerComplete: creative?.trackerComplete ?? '',
    variants: creative?.variants.map((variant) => ({ ...variant })) ?? [],
    file: null,
  };
}

export function applyCreativeBannerSize(value: CreativeFormValue, bannerSize: string) {
  const size = findCreativeBannerSize(bannerSize);
  return {
    ...value,
    bannerSize,
    width: size ? String(size.width) : '',
    height: size ? String(size.height) : '',
  };
}

export function serializeCreativeFormValue(value: CreativeFormValue): CreativeMutationPayload {
  const payload: CreativeMutationPayload = {
    name: value.name.trim(),
    type: value.type,
    activeStatus: value.activeStatus,
    impressionTrackers: value.impressionTrackers.map((item) => item.trim()).filter(Boolean),
  };
  if (value.type === CreativeType.Display) {
    payload.subType = value.subType;
    payload.width = positiveNumberOrNull(value.width);
    payload.height = positiveNumberOrNull(value.height);
    payload.bannerSize = value.bannerSize;
    payload.content = value.subType === CreativeSubType.Html ? value.content.trim() : '';
    payload.clickThroughUrl =
      value.subType === CreativeSubType.Image ? value.clickThroughUrl.trim() : '';
    return payload;
  }
  payload.hosted = value.hosted;
  payload.clickThroughUrl =
    value.hosted === CreativeHostedMode.Shigo ? value.clickThroughUrl.trim() : '';
  payload.vastTag = value.hosted === CreativeHostedMode.VastTag ? value.vastTag.trim() : '';
  payload.vastUrl = value.hosted === CreativeHostedMode.VastUrl ? value.vastUrl.trim() : '';
  if (value.file) payload.creative = value.file.name;
  payload.variants = value.variants.map((variant) => ({ ...variant }));
  copyTrackers(payload, value);
  return payload;
}

function addIssue(context: z.RefinementCtx, path: keyof CreativeFormValue, message: string) {
  context.addIssue({ code: 'custom', path: [path], message });
}

function isValidOptionalUrl(value: string) {
  if (!value.trim()) return true;
  return /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w/\-.?%&=;:{}[\]]*)?$/.test(value.trim());
}

function numberString(value: number | undefined) {
  return value === undefined ? '' : String(value);
}

function positiveNumberOrNull(value: string) {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(dashboardT('creatives.validation.dimension'));
  }
  return parsed;
}

function isValidOptionalPositiveNumber(value: string) {
  if (!value.trim()) return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function copyTrackers(payload: CreativeMutationPayload, value: CreativeFormValue) {
  const keys = [
    'trackerStart',
    'trackerFirstQuartile',
    'trackerMidpoint',
    'trackerThirdQuartile',
    'trackerComplete',
  ] as const;
  keys.forEach((key) => {
    payload[key] = value[key].trim();
  });
}
